import { httpClient } from "./client";
import type { operations, Schema } from "../types/generated";

export type TournamentMatchKind = Schema["TournamentMatchRef"]["kind"];
export type TournamentPresenceCommand = Schema["TournamentPresenceCommand"];
export type TournamentPresenceBatch = Schema["TournamentPresenceBatch"];
export type TournamentExecutionCommand = Schema["TournamentExecutionCommand"];
export type TournamentScoreCommand = Schema["TournamentScoreCommand"];
export type TournamentMatchDispatchCommand =
  Schema["TournamentMatchDispatchCommand"];
export type TournamentDisplayProvisionCommand =
  Schema["TournamentDisplayProvisionCommand"];
export type TournamentDisplaySetupLinkCommand =
  Schema["TournamentDisplaySetupLinkCommand"];
export type TournamentDisplayRevokeCommand =
  Schema["TournamentDisplayRevokeCommand"];
export type TournamentDisplayControlCommand =
  Schema["TournamentDisplayControlCommand"];
export type TournamentDisplayAnnouncementCommand =
  Schema["TournamentDisplayAnnouncementCommand"];
export type TournamentDisplayAnnouncementDeleteCommand =
  Schema["TournamentDisplayAnnouncementDeleteCommand"];
export type TournamentDisplayExchangeRequest =
  Schema["TournamentDisplayExchangeRequest"];
type OperatorScheduleQuery = NonNullable<
  operations["get_tournament_operator_schedule_tournaments__tournament_id__live_operator_schedule_get"]["parameters"]["query"]
>;
export type TournamentOperatorScheduleParams = {
  day?: OperatorScheduleQuery["day"];
  eventId?: OperatorScheduleQuery["event_id"];
  drawId?: OperatorScheduleQuery["draw_id"];
  courtId?: OperatorScheduleQuery["court_id"];
  query?: OperatorScheduleQuery["query"];
  cursor?: OperatorScheduleQuery["cursor"];
  limit?: OperatorScheduleQuery["limit"];
};
type OperatorBoardQuery = NonNullable<
  operations["get_tournament_operator_board_tournaments__tournament_id__live_operator_board_get"]["parameters"]["query"]
>;
export type TournamentOperatorBoardParams = {
  day: OperatorBoardQuery["day"];
  windowStartAt?: OperatorBoardQuery["window_start_at"];
  windowMinutes?: OperatorBoardQuery["window_minutes"];
  eventId?: OperatorBoardQuery["event_id"];
  drawId?: OperatorBoardQuery["draw_id"];
  courtId?: OperatorBoardQuery["court_id"];
  query?: OperatorBoardQuery["query"];
};

export const tournamentDayApi = {
  selfCheckInTournament: (
    tournamentId: string,
    command: TournamentPresenceCommand,
  ) =>
    httpClient.post<Schema["TournamentParticipantPresence"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/presence/self`,
      command,
    ),

  getTournamentPresence: (
    tournamentId: string,
    params: { limit?: number; cursor?: string } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.cursor !== undefined) search.set("cursor", params.cursor);
    const query = search.toString();
    return httpClient.get<Schema["TournamentPresencePage"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/presence${query ? `?${query}` : ""}`,
    );
  },

  applyTournamentPresenceBatch: (
    tournamentId: string,
    batch: TournamentPresenceBatch,
  ) =>
    httpClient.post<Schema["TournamentPresenceBatchResult"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/presence/batches`,
      batch,
    ),

  getTournamentExecution: (kind: TournamentMatchKind, matchId: string) =>
    httpClient.get<Schema["TournamentMatchExecutionProjection"]>(
      `/tournaments/matches/${kind}/${encodeURIComponent(matchId)}/execution`,
    ),

  getTournamentScoreState: (kind: TournamentMatchKind, matchId: string) =>
    httpClient.get<Schema["TournamentScoreState"]>(
      `/tournaments/matches/${kind}/${encodeURIComponent(matchId)}/score`,
    ),

  applyTournamentExecutionCommand: (
    kind: TournamentMatchKind,
    matchId: string,
    command: TournamentExecutionCommand,
  ) =>
    httpClient.post<Schema["TournamentMatchExecutionProjection"]>(
      `/tournaments/matches/${kind}/${encodeURIComponent(matchId)}/execution-commands`,
      command,
    ),

  applyTournamentScoreCommand: (
    kind: TournamentMatchKind,
    matchId: string,
    command: TournamentScoreCommand,
  ) =>
    httpClient.post<Schema["TournamentScoreState"]>(
      `/tournaments/matches/${kind}/${encodeURIComponent(matchId)}/score-commands`,
      command,
    ),

  applyTournamentDispatchCommand: (
    kind: TournamentMatchKind,
    matchId: string,
    command: TournamentMatchDispatchCommand,
  ) =>
    httpClient.post<Schema["TournamentMatchExecutionProjection"]>(
      `/tournaments/matches/${kind}/${encodeURIComponent(matchId)}/dispatch-commands`,
      command,
    ),

  getTournamentOperatorLive: (tournamentId: string) =>
    httpClient.get<Schema["TournamentOperatorLiveSnapshot"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/operator`,
    ),

  getTournamentOperatorRecovery: (tournamentId: string) =>
    httpClient.get<Schema["TournamentOperatorRecoverySnapshot"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/operator/recovery`,
    ),

  getTournamentOperatorMatchInspector: (
    tournamentId: string,
    kind: TournamentMatchKind,
    matchId: string,
  ) =>
    httpClient.get<Schema["TournamentOperatorMatchInspector"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/operator/matches/${kind}/${encodeURIComponent(matchId)}`,
    ),

  getTournamentOperatorSchedule: (
    tournamentId: string,
    params: TournamentOperatorScheduleParams = {},
  ) => {
    const search = new URLSearchParams();
    if (params.day) search.set("day", params.day);
    if (params.eventId) search.set("event_id", params.eventId);
    if (params.drawId) search.set("draw_id", params.drawId);
    if (params.courtId) search.set("court_id", params.courtId);
    if (params.query) search.set("query", params.query);
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    const query = search.toString();
    return httpClient.get<Schema["TournamentOperatorSchedulePage"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/operator/schedule${query ? `?${query}` : ""}`,
    );
  },

  getTournamentOperatorBoard: (
    tournamentId: string,
    params: TournamentOperatorBoardParams,
  ) => {
    const search = new URLSearchParams();
    search.set("day", params.day);
    if (params.windowStartAt)
      search.set("window_start_at", params.windowStartAt);
    if (params.windowMinutes !== undefined)
      search.set("window_minutes", String(params.windowMinutes));
    if (params.eventId) search.set("event_id", params.eventId);
    if (params.drawId) search.set("draw_id", params.drawId);
    if (params.courtId) search.set("court_id", params.courtId);
    if (params.query) search.set("query", params.query);
    const query = search.toString();
    return httpClient.get<Schema["TournamentOperatorBoard"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/operator/board?${query}`,
    );
  },

  getMyTournamentLive: (tournamentId: string) =>
    httpClient.get<Schema["TournamentParticipantLiveSnapshot"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/me`,
    ),

  getMyTournamentLiveSchedule: (
    tournamentId: string,
    params: { cursor?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    const query = search.toString();
    return httpClient.get<Schema["TournamentParticipantSchedulePage"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/schedule${query ? `?${query}` : ""}`,
    );
  },

  getTournamentPublicLive: (tournamentId: string) =>
    httpClient.get<Schema["TournamentPublicLiveSnapshot"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live`,
    ),

  getTournamentPublicLiveSchedule: (
    tournamentId: string,
    params: { cursor?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    const query = search.toString();
    return httpClient.get<Schema["TournamentPublicSchedulePage"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/public/schedule${query ? `?${query}` : ""}`,
    );
  },

  exchangeTournamentDisplay: (request: TournamentDisplayExchangeRequest) =>
    httpClient.post<Schema["TournamentDisplayExchangeStart"]>(
      "/tournaments/display-capabilities/exchange",
      request,
    ),

  getTournamentDisplayLive: (tournamentId: string) =>
    httpClient.get<Schema["TournamentDisplayLiveSnapshot"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/live/display`,
    ),

  provisionTournamentDisplay: (
    tournamentId: string,
    command: TournamentDisplayProvisionCommand,
  ) =>
    httpClient.post<Schema["TournamentDisplayProvision"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays`,
      command,
    ),

  listTournamentDisplays: (tournamentId: string) =>
    httpClient.get<Schema["TournamentDisplayProfile"][]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays`,
    ),

  createTournamentDisplaySetupLink: (
    tournamentId: string,
    displayId: string,
    command: TournamentDisplaySetupLinkCommand,
  ) =>
    httpClient.post<Schema["TournamentDisplaySetupLink"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays/${encodeURIComponent(displayId)}/setup-links`,
      command,
    ),

  applyTournamentDisplayControl: (
    tournamentId: string,
    displayId: string,
    command: TournamentDisplayControlCommand,
  ) =>
    httpClient.post<Schema["TournamentDisplayProfile"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays/${encodeURIComponent(displayId)}/commands`,
      command,
    ),

  createTournamentDisplayAnnouncement: (
    tournamentId: string,
    displayId: string,
    command: TournamentDisplayAnnouncementCommand,
  ) =>
    httpClient.post<Schema["TournamentPublicAnnouncement"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays/${encodeURIComponent(displayId)}/announcements`,
      command,
    ),

  deleteTournamentDisplayAnnouncement: (
    tournamentId: string,
    displayId: string,
    announcementId: string,
    command: TournamentDisplayAnnouncementDeleteCommand,
  ) =>
    httpClient.deleteWithBody<Schema["TournamentDisplayProfile"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays/${encodeURIComponent(displayId)}/announcements/${encodeURIComponent(announcementId)}`,
      command,
    ),

  revokeTournamentDisplay: (
    tournamentId: string,
    displayId: string,
    command: TournamentDisplayRevokeCommand,
  ) =>
    httpClient.deleteWithBody<Schema["TournamentDisplayProfile"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/displays/${encodeURIComponent(displayId)}`,
      command,
    ),
};
